import React, { ReactElement, useEffect, useState } from "react";
import store from "../../../../redux/store";
import { Modal, ModalBody, ModalHeader } from "@adeccoux/tag-ds";
import { CURRENT_LOCALE, toLiteral, getLanguageString } from "../../../../helper/locale-utils";
import Results from "../../../shared/results/results";
import SearchInput from "../../../shared/forms/search-input";
import "./literals-configuration.scss";
import * as Api from "../../../../../src/api";
import { filter } from "lodash";
import { isSuperAdmin } from "../../../../helper/role-helper";
import ActionsButtonsTable from "../../../shared/actions-buttons-table/actions-buttons-table";
import { DEFAULT_LITERAL_LANGUAGE, literalsToSkip } from "../../../administrator/literals-management/constants";
import { literalDetailsColumns } from "../../../../constants/resultColumns";
import LiteralEditForm from "./literal-edit-form";

const LiteralsConfiguration: React.FC<any> = (props): ReactElement => {
  const { language, setOpenLiteralsConfiguration } = props;

  const [refresh, setRefresh] = useState(1);
  const [editLiteral, setEditLiteral] = useState<any>(undefined);
  const [customTenantLiteral, setCustomTenantLiteral] = useState<any>(undefined);
  const [literals, setLiterals] = useState([]);
  const [tenantsInUse, setTenantsInUse] = useState([]);

  const tenantStore = store?.getState()?.tenant;
  const tenant = tenantStore?.selectedTenant;
  const isDefaultFilter = CURRENT_LOCALE === DEFAULT_LITERAL_LANGUAGE;

  useEffect(() => {
    trimAndSetLiterals();
    fetchLiteralStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function trimAndSetLiterals() {
    const currentRouteLiterals = store?.getState()?.literalsConfiguration?.configurations?.current_route_literals;
    const currentRouteId = store?.getState()?.navigation?.currentRoute?.id;
    const currentLiterals = currentRouteLiterals?.[currentRouteId];
    const skipSet = new Set(literalsToSkip);
    const finalLiterals = currentLiterals.filter((literal: any) => !skipSet.has(literal));
    setLiterals(finalLiterals);
  }

  const fetchLiterals = async (filters: any) => {
    const { search } = filters;
    filters.language = CURRENT_LOCALE;
    filters.literalKeys = literals.filter((literal: string) => literal !== "");
    filters.perPage = 6;
    if (search) {
      const filteredLiterals = filter(literals, (literal: string) => literal.toLowerCase().includes(search.toLowerCase()));
      filters.literalKeys = filteredLiterals;
    }
    const response = await Api.fetchCountryLiterals(filters);
    return { count: response.totalCount, data: response.literals };
  };

  const fetchLiteralStats = async () => {
    const response = await Api.fetchTranslationStats({ search: "" });
    const currentLanguage = CURRENT_LOCALE  
    const currentTenants = response?.literals?.translationStats.find((translation: any) => translation.language === currentLanguage);
    setTenantsInUse(currentTenants?.tenantInUse);
  };

  const renderCellContent = (col: any, res: any) => {
    switch (col?.columnKey) {
      case "value": {
        return <div>{res?.translation?.tenantCustom?.value ?? res?.translation?.value ?? res?.value ?? "-"}</div>;
      }
      case "translation": {
        const translatedLiteral = toLiteral({ id: "admin.settings.literal.management.translated.status2" });
        const usingDefaultLiteral = `${toLiteral({ id: "admin.settings.literal.management.using.default2" })} (${DEFAULT_LITERAL_LANGUAGE})`;
        const customizedLiteral = `${toLiteral({ id: "admin.settings.literal.management.using.custom2" })} (${tenant?.name})`;
        const literalStatus = res?.translation?.tenantCustom
          ? customizedLiteral
          : isDefaultFilter
          ? toLiteral({ id: "admin.settings.literal.management.translated.default" })
          : res?.translation
          ? translatedLiteral
          : usingDefaultLiteral;
        return <div className={`status-tag ${res?.translation?.tenantCustom ? "alert" : res?.translation || isDefaultFilter ? "success" : "error"}`}>{literalStatus}</div>;
      }
      case "actions": {
        if (!isSuperAdmin() && res?.complex) return <div className={`status-tag info`}>{toLiteral({ id: "admin.manage.literals.complex.info" })}</div>;
        const editElement =
          res?.translation?.tenantCustom || (isDefaultFilter && !isSuperAdmin())
            ? undefined
            : (e: any) => {
                setEditLiteral(e);
                setCustomTenantLiteral(undefined);
              };
        const settingsElement = res?.translation
          ? (e: any) => {
              setCustomTenantLiteral(e);
              setEditLiteral(undefined);
            }
          : undefined;
        return (
          <ActionsButtonsTable
            result={res}
            editElement={editElement}
            settingsElement={settingsElement}
            editElementTooltipText={res?.translation ? toLiteral({ id: "admin.settings.literal.management.action.edit2" }) : toLiteral({ id: "admin.settings.literal.management.action.translate2" })}
            settingsElementTooltipText={toLiteral({ id: "admin.settings.literal.management.action.customize2" })}
            deleteElementTooltipText={toLiteral({ id: "admin.settings.literal.management.action.delete2" })}
          />
        );
      }
      default:
        return <div>{res?.[col?.columnKey] ?? "-"}</div>;
    }
  };

  const renderItems = (results: any, columns?: any) => {
    if (results?.data)
      return (
        <>
          {results?.data?.map((res: any, resId: number) => {
            return (
              <tr key={`row-${res?.id || resId}`}>
                {columns?.map((col: any) => (
                  <td key={col?.columnKey} id={"literals-list-" + col?.columnKey + "-" + (res?.id || resId)}>
                    {renderCellContent(col, res)}
                  </td>
                ))}
              </tr>
            );
          })}
        </>
      );
    return null;
  };
  return (
    <Modal id="literals-configuration" renderAsPortal open onClose={() => setOpenLiteralsConfiguration(false)}>
      <ModalHeader>
        <h4>{toLiteral({ id: "home.literals.management.modal.title.viewLiteral2" })}</h4>
      </ModalHeader>
      <div>
        {toLiteral({ id: "admin.literal.management.countryLiteral.translation.message.3" })} {getLanguageString(language)}
      </div>
      <ModalBody>
        <div className="g-container_12">
          <div className="g-item_7 pr4">
            <Results
              refresh={refresh}
              columns={literalDetailsColumns}
              defaultColumns={literalDetailsColumns}
              fetchResults={fetchLiterals}
              renderItems={renderItems}
              filterOnChange
              hideTableConfiguration
              hideFiltersSummary
              disablePagination={false}
            >
              <SearchInput data-testid="literals-search-input" id="literals-search-input" small className="mt3 mb2" placeholder={"home.search"} name="search" type="text" preventSubmit={true} />
            </Results>
          </div>
          <div className="g-item_5 mt3">
            <LiteralEditForm
              language={language}
              literal={editLiteral || customTenantLiteral}
              customTenantLiteral={customTenantLiteral}
              setRefresh={setRefresh}
              refresh={refresh}
              disableForm={!(editLiteral || customTenantLiteral)}
              tenantsInUse={tenantsInUse}
              onCancel={() => {
                setEditLiteral(undefined);
                setCustomTenantLiteral(undefined);
              }}
            />
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default LiteralsConfiguration;
